/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Global search (GET /search) result helpers, shared by the classic navbar
 * search and the UI v2 command palette so the two can never disagree about
 * which hits a tenant sees or where a hit opens.
 */

export const TRAVEL_TYPE_LABELS = {
  trips: 'Destination',
  trip_packages: 'Trip Package',
  travel_orders: 'Travel Order',
  package_components: 'Trip Logistics',
  departure_dates: 'Departure Date',
  payment_templates: 'Payment Template',
  travel_order_pax: 'Passenger',
};

/** Legacy commerce search hits hidden when Travel UI is on. */
export const LEGACY_CRM_TYPE_LABELS = {
  products: 'Product',
  orders: 'Order',
  quotations: 'Quotation',
};

/**
 * Travel hits only for travel tenants; legacy commerce hits only for the rest.
 * keepLegacyCommerce (the v2 palette) keeps orders / products / quotations for
 * travel tenants too: v2 serves them from the Sales app.
 */
export function filterSearchResults(results, showTravel, { keepLegacyCommerce = false } = {}) {
  return (Array.isArray(results) ? results : []).filter((result) => {
    const type = result?.type;
    if (!showTravel && TRAVEL_TYPE_LABELS[type]) return false;
    if (showTravel && !keepLegacyCommerce && LEGACY_CRM_TYPE_LABELS[type]) return false;
    return true;
  });
}

/** Where a hit opens, or null when the type has no page. */
export function searchResultPath(result) {
  switch (result.type) {
    case 'leads':
      return `/leads/${result.id}`;
    case 'users':
      return `/users/${result.id}/profile`;
    case 'contacts':
      return `/contacts/${result.id}`;
    case 'deals':
      return `/deals/${result.id}`;
    case 'products':
      return `/products/${result.id}`;
    case 'accounts':
      return `/accounts/${result.id}`;
    case 'orders':
      return `/orders/${result.id}`;
    case 'invoices':
      return `/invoices`;
    case 'quotation':
      return `/quote/${result.id}`;
    case 'case':
    case 'cases':
      return `/cases/${result.id}`;
    case 'trips':
      return `/trips/detail/${result.id}`;
    case 'trip_packages':
      return `/trip-packages/${result.id}`;
    case 'travel_orders':
      return `/travel-order/${result.id}`;
    case 'package_components':
      return `/trip-package-components/${result.id}`;
    case 'departure_dates':
      return `/trip-departure-dates/${result.id}`;
    case 'payment_templates':
      return `/payment-templates/${result.id}`;
    case 'travel_order_pax':
      return `/travel-order-pax/${result.id}`;
    default:
      return null;
  }
}

export const getResultText = (result) => {
  switch (result.type) {
    case 'leads': {
      const firstName = result.first_name || '';
      const lastName = result.last_name || '';
      const companyName = result.company_name || '';
      
      const name = [firstName, lastName].filter(Boolean).join(' ');
      return companyName ? `${name} - ${companyName}` : name;
    }

    case 'users': {
      const username = result.username || '';
      const email = result.email || '';
      return email ? `${username} (${email})` : username;
    }

    case 'deals':
      return result.name || '';

    case 'products': {
      const name = result.name || '';
      const price = result.price ? `$${result.price}` : '';
      return price ? `${name} - ${price}` : name;
    }

    case 'accounts': {
      const name = result.name || '';
      const accountNumber = result.account_number || '';
      return accountNumber ? `${name} (${accountNumber})` : name;
    }

    case 'contacts': {
      const firstName = result.first_name || '';
      const lastName = result.last_name || '';
      return [firstName, lastName].filter(Boolean).join(' ');
    }

    case 'orders':
      return result.order_number || '';

    case 'invoices':
      return result.invoice_number || '';

    case 'quotation':
      return result.quote_name || '';

    case 'case':
    case 'cases':
      return result.case_number || '';

    case 'trips': {
      const country = result.primary_country || '';
      const title = result.title || '';
      const continent = result.continent || '';
      const name = result.name || country || title;
      if (continent && name) return `${name} (${continent})`;
      return name;
    }

    case 'trip_packages': {
      const title = result.title || result.name || '';
      const destination = result.trip_country || result.trip_title || '';
      return destination ? `${title} (${destination})` : title;
    }

    case 'travel_orders':
      return result.name || '';

    case 'package_components': {
      const name = result.name || '';
      const category = result.category || '';
      return category ? `${name} — ${category}` : name;
    }

    case 'departure_dates': {
      const city = result.departure_city || '';
      const destination = result.trip_country || result.trip_title || '';
      const date = result.departure_date
        ? new Date(result.departure_date).toLocaleDateString()
        : '';
      const parts = [destination, city, date].filter(Boolean);
      return parts.join(' · ') || result.name || '';
    }

    case 'payment_templates':
      return result.template_name || result.name || '';

    case 'travel_order_pax': {
      const paxName =
        result.passenger_name ||
        [result.first_name, result.last_name].filter(Boolean).join(' ') ||
        result.name ||
        '';
      const orderName = result.travel_order_name || '';
      return orderName ? `${paxName} (${orderName})` : paxName;
    }

    default:
      return result.name || result.title || '';
  }
};

export const getTypeLabel = (type) => {
  if (TRAVEL_TYPE_LABELS[type]) return TRAVEL_TYPE_LABELS[type];
  if (!type) return '';
  return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
};
